<template>
  <div class="app-shell">
    <left-nav
      :points-balance="user.pointsBalance"
      :is-login="user.isLogin"
      :avatar="user.avatar"
      :default-avatar="user.defaultAvatar || siteConfig.defaultAvatar"
      :service-qr="user.serviceQr || siteConfig.serviceQr"
      :logo-title="siteConfig.logoTitle"
      :logo-image="siteConfig.logoImage"
      :user-id="user.id"
      :user-mobile="user.mobile"
      @open-login="showLogin = true"
      @open-points="showPointsRecord = true"
      @open-recharge="showRecharge = true"
      @open-password="showPassword = true"
      @navigate="handleNavigate"
      @logout="handleLogout"
    />
    <div class="content-area">
      <router-view />
    </div>

    <login-modal
      :visible="showLogin"
      @close="showLogin = false"
      @submit="handleLogin"
    />
    <password-modal :visible="showPassword" @close="showPassword = false" />
    <points-record-modal
      :visible="showPointsRecord"
      :records="pointsRecords"
      :has-more-remote="pointsHasMore"
      :loading-more-remote="pointsLoadingMore"
      @close="showPointsRecord = false"
      @load-more-remote="loadMorePointsRecords"
    />
    <recharge-modal
      :visible="showRecharge"
      :plans="rechargePlans"
      :loading="rechargePlansLoading"
      @close="showRecharge = false"
      @buy="handleRecharge"
    />
  </div>
</template>

<script>
import LeftNav from "./components/LeftNav.vue";
import LoginModal from "./components/LoginModal.vue";
import PasswordModal from "./components/PasswordModal.vue";
import PointsRecordModal from "./components/PointsRecordModal.vue";
import RechargeModal from "./components/RechargeModal.vue";
import { mockUser } from "./mock/data";
import { userApi } from "./api/user";
import { openCashPayWindow } from "./utils/cashPayOpenUrl";

export default {
  name: "App",
  components: {
    LeftNav,
    LoginModal,
    PasswordModal,
    PointsRecordModal,
    RechargeModal
  },
  data() {
    return {
      showLogin: false,
      showPassword: false,
      showPointsRecord: false,
      showRecharge: false,
      user: { ...mockUser },
      siteConfig: {
        logoTitle: "漫剧",
        logoImage: "",
        siteIco: "",
        serviceQr: "",
        defaultAvatar: ""
      },
      pointsRecords: [],
      pointsHasMore: false,
      pointsNextOffset: 0,
      pointsLoadingMore: false,
      rechargePlans: [],
      rechargePlansLoading: false
    };
  },
  created() {
    this.fetchPublicSiteConfig();
    this.bootstrapUserState();
    this.consumeLoginPopupFlag();
    // 允许页面内直接唤起登录弹窗（例如 Home 的「创建项目」）
    this.$root.$on("open-login", () => {
      this.showLogin = true;
    });
    // 业务页面（步骤2/步骤4）生成成功后触发，统一刷新积分与明细
    this.$root.$on("points-changed", this.handlePointsChanged);
  },
  beforeDestroy() {
    this.$root.$off("points-changed", this.handlePointsChanged);
  },
  watch: {
    showRecharge(val) {
      if (val) {
        this.fetchRechargePlans();
      }
    },
    showPointsRecord(val) {
      if (val) {
        this.refreshPointsState();
      }
    }
  },
  methods: {
    applySiteIco(url) {
      const u = String(url || "").trim();
      if (!u) return;
      const href = /^https?:\/\//i.test(u) ? u : (u.startsWith("/") ? u : `/${u}`);
      let el = document.querySelector("link[rel='icon']");
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "icon");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    },
    async fetchPublicSiteConfig() {
      try {
        const d = await userApi.setupDictionary();
        const site = d && d.site && typeof d.site === "object" ? d.site : {};
        this.siteConfig = {
          logoTitle: String(site.logoTitle || "漫剧"),
          logoImage: String(site.logoImage || ""),
          siteIco: String(site.siteIco || ""),
          serviceQr: String(site.serviceQr || ""),
          defaultAvatar: String(site.defaultAvatar || "")
        };
        this.applySiteIco(this.siteConfig.siteIco);
      } catch (e) {
        // ignore
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async pollRechargePaid(outTradeNo, maxRounds = 24, intervalMs = 2500) {
      if (!outTradeNo) return false;
      for (let i = 0; i < maxRounds; i += 1) {
        try {
          const order = await userApi.queryRechargeOrder({ outTradeNo });
          if (order && order.status === "paid") {
            await this.refreshPointsState();
            return true;
          }
          if (order && (order.status === "failed" || order.status === "closed")) {
            return false;
          }
        } catch (e) {
          // 忽略瞬时网络错误，继续轮询
        }
        await this.sleep(intervalMs);
      }
      return false;
    },
    async refreshPointsState() {
      if (!localStorage.getItem("user_token")) return;
      try {
        const [profile, raw] = await Promise.all([
          userApi.profile(),
          userApi.pointsRecords({ offset: 0, limit: 50 })
        ]);
        this.user = {
          ...this.user,
          ...profile,
          isLogin: true
        };
        if (Array.isArray(raw)) {
          this.pointsRecords = raw;
          this.pointsHasMore = false;
          this.pointsNextOffset = raw.length;
        } else {
          const list = raw && Array.isArray(raw.records) ? raw.records : [];
          this.pointsRecords = list;
          this.pointsHasMore = !!(raw && raw.hasMore);
          this.pointsNextOffset =
            raw && raw.nextOffset != null ? raw.nextOffset : list.length;
        }
      } catch (e) {
        // 静默失败，避免影响主流程交互
      }
    },
    async loadMorePointsRecords() {
      if (!localStorage.getItem("user_token")) return;
      if (!this.pointsHasMore || this.pointsLoadingMore) return;
      this.pointsLoadingMore = true;
      try {
        const raw = await userApi.pointsRecords({
          offset: this.pointsNextOffset,
          limit: 50
        });
        const batch = Array.isArray(raw) ? raw : raw && raw.records ? raw.records : [];
        if (Array.isArray(raw)) {
          this.pointsHasMore = false;
        } else {
          this.pointsHasMore = !!(raw && raw.hasMore);
          this.pointsNextOffset =
            raw && raw.nextOffset != null ? raw.nextOffset : this.pointsNextOffset + batch.length;
        }
        const seen = new Set(this.pointsRecords.map(r => r.id));
        batch.forEach(row => {
          if (row && !seen.has(row.id)) {
            seen.add(row.id);
            this.pointsRecords.push(row);
          }
        });
      } catch (e) {
        // ignore
      } finally {
        this.pointsLoadingMore = false;
      }
    },
    handlePointsChanged() {
      this.refreshPointsState();
    },
    consumeLoginPopupFlag() {
      const flag = sessionStorage.getItem("need_login_popup");
      if (flag === "1") {
        sessionStorage.removeItem("need_login_popup");
        this.showLogin = true;
      }
    },
    async bootstrapUserState() {
      // App 启动时尝试恢复登录态，并同步积分记录到全局弹窗。
      const token = localStorage.getItem("user_token");
      if (!token) return;
      try {
        await this.refreshPointsState();
      } catch (e) {
        localStorage.removeItem("user_token");
      }
    },
    handleNavigate(path) {
      // 点击 Logo 回首页不需要登录
      if (path === "/home") {
        if (this.$route.path !== path) {
          this.$router.push(path);
        }
        return;
      }
      if (!this.user.isLogin) {
        this.showLogin = true;
        sessionStorage.setItem("pending_nav", path);
        return;
      }
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    async handleLogin(formData) {
      try {
        const result = await userApi.login(formData);
        localStorage.setItem("user_token", result.token);
        this.user = { ...this.user, ...result.user, isLogin: true };
        await this.refreshPointsState();
        this.showLogin = false;
        const pending = sessionStorage.getItem("pending_nav");
        if (pending) {
          sessionStorage.removeItem("pending_nav");
          if (this.$route.fullPath !== pending) {
            this.$router.push(pending);
          }
        }
      } catch (e) {
        window.alert(e.message || "登录失败");
      }
    },
    handleLogout() {
      localStorage.removeItem("user_token");
      this.user.isLogin = false;
      this.user.name = "未登录";
    },
    async fetchRechargePlans() {
      this.rechargePlansLoading = true;
      try {
        const list = await userApi.rechargePlans();
        this.rechargePlans = Array.isArray(list) ? list : [];
      } catch (e) {
        this.rechargePlans = [];
        window.alert(e.message || "加载充值套餐失败");
      } finally {
        this.rechargePlansLoading = false;
      }
    },
    async handleRecharge(plan) {
      // 充值入口挂在全局导航上，这里统一处理登录拦截和积分刷新。
      if (!this.user.isLogin) {
        window.alert("请先登录");
        this.showLogin = true;
        return;
      }
      const pts = Number(plan && plan.points);
      if (!plan || Number.isNaN(pts) || pts <= 0) {
        window.alert("套餐数据无效");
        return;
      }
      try {
        const body =
          plan.id != null && plan.id !== "" && Number(plan.id) > 0
            ? { planId: Number(plan.id), payType: String(plan.payType || "wechat") }
            : { points: pts, payType: String(plan.payType || "wechat") };
        const res = await userApi.recharge(body);
        const opened = openCashPayWindow(res && res.payPayload, body.payType);
        this.showRecharge = false;
        if (opened) {
          window.alert("订单已创建，请在新窗口完成支付。系统正在自动确认到账。");
          const paid = await this.pollRechargePaid(res.outTradeNo);
          if (paid) {
            window.alert("支付成功，积分已到账。");
            return;
          }
          await this.refreshPointsState();
        } else {
          const prov = res && res.payPayload && res.payPayload.provider;
          window.alert(
            prov && prov !== "mock"
              ? "订单已创建，但未生成支付链接。请在后台「系统配置 → AI接口配置」勾选所选支付方式（微信/支付宝）并填全密钥/证书后重新下单。"
              : "订单已创建，当前为模拟支付模式：请在后台勾选微信/支付宝并保存；联调可访问接口返回的 mockPayUrl 完成模拟到账。"
          );
        }
      } catch (e) {
        window.alert(e.message || "充值失败");
      }
    }
  }
};
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  background: #111111;
  display: flex;
  color: #ffffff;
}

.content-area {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding: 20px 10px 0px 24px;
  margin-left: 75px;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .content-area {
    padding: 16px 10px 20px 12px;
  }
}
</style>
